import DashboardBonuses from '../components/DashboardBonuses';
import {comparisonChart} from '../components/dashboardChart';
import './DashboardOverview.css';
import React, { useState, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import DashboardPeriodPicker from '../components/DashboardPeriodPicker';
import DashboardChannels from '../components/DashboardChannels';
import { Link } from 'react-router-dom';
import { analyticsAPI } from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { TrendingUp, TrendingDown, Sparkles, X, HelpCircle } from 'lucide-react';

export const METRIC_HELP = {
  'Выручка по программе': 'Текущая сумма подтверждённых заказов, созданных в выбранный период, за вычетом оформленных возвратов. Это пересчитанная сумма заказов, а не денежный поток по дате возврата. Тестовые клиенты исключены.',
  'Бонусы и связь, оценка': 'Подтверждённая оплата бонусами (до возвратов), без начислений. К ней добавляется стоимость отправленных сообщений завершённых кампаний по тарифам интеграций. Не бухгалтерские расходы и не подтверждённая себестоимость. Начисления отдельно в блоке бонусов.',
  'Новые карточки': 'Количество карточек клиентов, созданных за выбранный период, включая импортированные. «Всего» — все клиенты аккаунта.',
  'Заказы': 'Количество подтверждённых заказов, созданных в выбранном периоде. Включает заказы с частичными возвратами; текущая выручка уменьшена на возвраты.',
  'Покупатели': 'Уникальные клиенты с подтверждёнными заказами в периоде.',
  'Средний чек': 'Выручка подтверждённых заказов, делённая на их количество, за выбранный период. Если заказов нет, значение равно нулю.',
  'Изменение': 'Изменение показателя в процентах относительно предыдущего периода такой же длительности. Если предыдущее значение равно нулю, процент не показывается.',
  'Сегменты': 'RFM — группы по давности, частоте и сумме покупок. «Простые» — сохранённые сегменты с заданными вами условиями. Клиент может входить в несколько простых сегментов.',
  'Качество клиентской базы': 'Количество и доля клиентов с заполненными данными или отмеченным согласием. Рассчитывается по всей базе аккаунта, независимо от периода. Отметка согласия отражает данные в карточке клиента.',
  'Демография': 'Распределение всей базы аккаунта по заполненным полу и дате рождения. Доли мужчин и женщин считаются среди клиентов с указанным полом; возрастные доли — среди показанных возрастных групп.',
};

export function HelpHint({ label, text }) {
  const id = useId();
  const button = useRef(null);
  const bubble = useRef(null);
  const timer = useRef(null);
  const [position, setPosition] = useState(null);
  const cancelClose = () => clearTimeout(timer.current);
  const close = () => { cancelClose(); setPosition(null); };
  const show = () => {
    cancelClose();
    const r = button.current.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 24);
    setPosition({ left: Math.max(12, Math.min(r.left, window.innerWidth - width - 12)), top: r.bottom + 8, width });
  };
  const leave = () => { cancelClose(); timer.current = setTimeout(close, 150); };
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (!position) return;
    const rect = bubble.current?.getBoundingClientRect();
    if (rect && rect.bottom > window.innerHeight - 12) {
      const top = Math.max(12, window.innerHeight - rect.height - 12);
      if (top !== position.top) setPosition(p => ({ ...p, top }));
    }
    const key = e => { if (e.key === 'Escape') close(); };
    const outside = e => { if (!button.current?.contains(e.target) && !bubble.current?.contains(e.target)) close(); };
    document.addEventListener('keydown', key);
    document.addEventListener('pointerdown', outside);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('keydown', key);
      document.removeEventListener('pointerdown', outside);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [position]);
  return <>
    <button ref={button} type="button" aria-label={`О показателе: ${label}`} aria-describedby={position ? id : undefined} aria-expanded={!!position}
      onMouseEnter={show} onMouseLeave={leave} onFocus={show} onBlur={close} onClick={show}
      style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 6, padding: 3, border: 0, background: 'transparent', color: 'var(--text-secondary)', cursor: 'help', borderRadius: 4 }}>
      <HelpCircle size={15} aria-hidden="true" />
    </button>
    {position && createPortal(<div ref={bubble} id={id} role="tooltip" onMouseEnter={cancelClose} onMouseLeave={leave}
      style={{ position: 'fixed', ...position, boxSizing: 'border-box', maxHeight: 'calc(100vh - 24px)', overflowY: 'auto', zIndex: 10000, padding: '12px 14px', borderRadius: 8, background: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-border)', color: 'var(--tooltip-text)', fontSize: 13, lineHeight: 1.5, boxShadow: '0 8px 24px #0006' }}>{text}</div>, document.body)}
  </>;
}


function fmt(v) {
  if (v == null) return '0';
  const n = Math.round(v);
  if (Math.abs(n) >= 1000000) return `${(n / 1000000).toFixed(1).replace('.0', '')} млн`;
  if (Math.abs(n) >= 10000) return n.toLocaleString('ru-RU');
  return n.toLocaleString('ru-RU');
}

function Change({ value }) {
  if (value == null) return null;
  const color = value >= 0 ? 'var(--trend-positive)' : 'var(--danger)';
  const Icon = value >= 0 ? TrendingUp : TrendingDown;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 13, fontWeight: 600, color }}>
      <Icon size={14} /> {value >= 0 ? '+' : ''}{value}%
    </span>
  );
}

function MetricCard({ title, value, href, suffix = '₽', change, sub, color = 'var(--text-primary)' }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 24px', flex: 1, minWidth: 200 }}>
      <div style={{ fontSize: 12, color: 'var(--text-caption)', marginBottom: 6 }}>{title}<HelpHint label={title} text={METRIC_HELP[title]} /></div>
      <div style={{ fontSize: 26, fontWeight: 800, fontFamily: 'var(--font-mono)', color }}><Link className="dashboard-metric-link" to={href||"/orders"}>{fmt(value)}</Link> {suffix && <span style={{ fontSize: 16, fontWeight: 600 }}>{suffix}</span>}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <Change value={change} />
        {change != null && <HelpHint label={`Изменение: ${title}`} text={METRIC_HELP['Изменение']} />}
        {sub && <span style={{ fontSize: 11, color: 'var(--text-caption)' }}>{sub}</span>}
      </div>
    </div>
  );
}

function QualityBar({ label, count, pct }) {
  return (
    <div className="dashboard-quality-row" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      <div style={{ flex: 1, fontSize: 12, color: 'var(--text-secondary)' }}>{label}</div>
      <div style={{ width: 120, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: pct > 70 ? '#22c55e' : pct > 40 ? '#f59e0b' : '#ef4444', borderRadius: 3 }} />
      </div>
      <div style={{ width: 60, textAlign: 'right', fontSize: 12, fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{count?.toLocaleString('ru-RU')}</div>
      <div style={{ width: 35, textAlign: 'right', fontSize: 11, color: 'var(--text-caption)' }}>{pct}%</div>
    </div>
  );
}

function SegmentsTable({ segments, mode, dates }) {
  if (!segments?.length) return <div style={{ color: 'var(--text-caption)', fontSize: 12, padding: 10 }}>Нет данных</div>;
  return (
    <table style={{ width: '100%', fontSize: 12 }}>
      <thead><tr style={{ color: 'var(--text-caption)' }}><td>Сегмент</td><td style={{ textAlign: 'right' }}>Клиентов</td><td style={{ textAlign: 'right' }}>Выручка</td><td style={{ textAlign: 'right' }}>Ср. чек</td></tr></thead>
      <tbody>
        {segments.map(s => (
          <tr key={s.name} style={{ borderBottom: '1px solid var(--border)' }}>
            <td style={{ padding: '6px 0' }}><span style={{ background: s.color, color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{s.name}</span></td>
            <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 11 }}><Link className="dashboard-segment-link" to={'/clients?'+new URLSearchParams({[mode==='rfm'?'rfm_segment':'saved_segment']:s.name})}>{(s.count || 0).toLocaleString('ru-RU')}</Link></td>
            <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 11 }}><Link className="dashboard-segment-link" to={'/orders?'+new URLSearchParams({...dates,status:'confirmed',[mode==='rfm'?'rfm_segments':'saved_segment']:s.name})}>{fmt(s.revenue)} ₽</Link></td>
            <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 11 }}>{fmt(s.avg_check)} ₽</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function DashboardPage() {
  const [range, setRange] = useState({period:'30d',from:'',to:''});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartMode, setChartMode] = useState('revenue');
  const [group,setGroup]=useState('day'),[compare,setCompare]=useState(true);
  const [segMode, setSegMode] = useState('rfm');
  const [showAI, setShowAI] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    let active=true;
    setLoading(true);
    const query=range.period==='custom'?`custom&date_from=${range.from}&date_to=${range.to}`:range.period;
    analyticsAPI.dashboardV2(query).then(r=>{if(active)setData(r.data);}).catch(()=>{}).finally(()=>{if(active)setLoading(false);});
    return()=>{active=false;};
  }, [range]);

  const loadAI = async () => {
    setShowAI(true);
    if (aiText) return;
    setAiLoading(true);
    try {
      const r = await analyticsAPI.insights();
      setAiText(r.data.insights);
    } catch { setAiText('Не удалось получить рекомендации'); }
    finally { setAiLoading(false); }
  };

  if (!data) return <div style={{ padding: 40, color: 'var(--text-caption)' }}>Загрузка...</div>;

  const c = data.cards;
  const q = data.quality;
  const demo = data.demographics || {};

  const query=range.period==='custom'?`custom&date_from=${range.from}&date_to=${range.to}`:range.period;
  const dates={date_from:data.bounds?.from||'',date_to:data.bounds?.to||''};
  const ordersHref='/orders?'+new URLSearchParams({...dates,status:'confirmed'});
  const plotted=comparisonChart(data,group);
  const chartKey = { revenue: 'revenue', orders: 'orders', clients: 'clients' }[chartMode];
  const chartLabel = { revenue: 'Выручка', orders: 'Заказы', clients: 'Клиенты' }[chartMode];
  const chartColor = { revenue: '#6366f1', orders: '#22c55e', clients: '#06b6d4' }[chartMode];

  const totalAge = (demo.age || []).reduce((s, a) => s + a.count, 0) || 1;

  return (
    <div className="animate-in" aria-busy={loading}>
      <div className="dashboard-main-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div><h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Дашборд</h1>{loading&&<span role="status" style={{fontSize:11,color:"var(--text-caption)"}}>Обновляем данные…</span>}</div>
        <div className="dashboard-main-toolbar" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-ghost btn-sm" onClick={loadAI} style={{ color: '#8b5cf6' }}>
            <Sparkles size={14} /> AI-рекомендации
          </button>
          <DashboardPeriodPicker value={range} onChange={setRange}/>

        </div>
      </div>

      {showAI && (
        <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 10, padding: 16, marginBottom: 20, position: 'relative' }}>
          <button onClick={() => setShowAI(false)} style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#7c3aed', marginBottom: 8 }}><Sparkles size={14} style={{ verticalAlign: 'middle' }} /> AI-рекомендации</div>
          {aiLoading ? <div style={{ color: 'var(--text-caption)' }}>Генерация...</div> : <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', color: '#374151' }}>{aiText}</div>}
        </div>
      )}

      <div className="dashboard-card-row" style={{ gap: 12, marginBottom: 20 }}>
        <MetricCard title="Выручка по программе" value={c.revenue.value} change={c.revenue.change} href={ordersHref} />
        <MetricCard title="Бонусы и связь, оценка" value={c.expenses.value} change={c.expenses.change} href={"/transactions?"+new URLSearchParams({...dates,kind:"hold_confirm"})} color="var(--danger)" />
        <MetricCard title="Новые карточки" value={c.registrations.value} suffix="" change={c.registrations.change} sub={`Всего ${c.registrations.total?.toLocaleString('ru-RU')}`} href={"/clients?"+new URLSearchParams(dates)} color="var(--text-primary)" />
        <MetricCard href={ordersHref} title="Средний чек" value={c.avg_check.value} change={c.avg_check.change} color="var(--warning)" />
        <MetricCard title="Заказы" href={ordersHref} value={c.orders.value} suffix="" change={c.orders.change}/>
        <MetricCard title="Покупатели" href={"/clients?"+new URLSearchParams({purchased_from:dates.date_from,purchased_to:dates.date_to})} value={c.buyers?.value} suffix=""/>
      </div>

      <div className="dashboard-top-grid" style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        <div className="card" style={{ flex: 2, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap:'wrap' }}>
              {['revenue', 'orders', 'clients'].map(m => (
                <button key={m} className={`btn btn-sm ${chartMode === m ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setChartMode(m)} style={{ fontSize: 11 }}>
                  {{ revenue: 'Выручка', orders: 'Заказы', clients: 'Клиенты' }[m]}
                </button>
              ))}
            </div>
          </div>
          <div className="dashboard-chart-controls"><select aria-label="Группировка графика" value={group} onChange={e=>setGroup(e.target.value)}><option value="day">По дням</option><option value="week">По 7 дней</option><option value="month">По месяцам</option></select><label><input type="checkbox" checked={compare} disabled={range.period==='all'} onChange={e=>setCompare(e.target.checked)}/> Предыдущий период</label></div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={plotted}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => { const d = new Date(v); return `${d.getDate()}.${String(d.getMonth()+1).padStart(2,'0')}`; }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}М` : v >= 1000 ? `${Math.round(v/1000)}K` : v} />
              <Tooltip formatter={(v,name) => [fmt(v) + (chartMode === 'revenue' ? ' ₽' : ''), name]} />
              {compare&&range.period!=='all'&&<Line name="Предыдущий период" dataKey={"previous_"+chartKey} stroke="var(--text-caption)" strokeDasharray="5 5" dot={false}/> }
              <Line name={chartLabel} type="monotone" dataKey={chartKey} stroke={chartColor} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
          <p className="dashboard-note">Сплошная линия — выбранный период, пунктир — предыдущий. Для покупателей при укрупнении показана сумма уникальных покупателей каждого дня.</p>
        </div>

        <div className="card" style={{ flex: 1, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Сегменты<HelpHint label="Сегменты" text={METRIC_HELP['Сегменты']} /></div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button className={`btn btn-sm ${segMode === 'rfm' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setSegMode('rfm')} style={{ fontSize: 10 }}>RFM</button>
              <button className={`btn btn-sm ${segMode === 'saved' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setSegMode('saved')} style={{ fontSize: 10 }}>Простые</button>
            </div>
          </div>
          <SegmentsTable segments={segMode === 'rfm' ? data.segments : data.saved_segments} mode={segMode} dates={dates}/>
          <Link className="dashboard-rfm-link" to="/segments?tab=rfm">Открыть карту RFM →</Link>
        </div>
      </div>

      <DashboardBonuses query={query} dates={dates}/>
      <DashboardChannels/>
      <div className="dashboard-bottom-grid" style={{ display: 'flex', gap: 16 }}>
        <div className="card" style={{ flex: 1, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Качество клиентской базы<HelpHint label="Качество клиентской базы" text={METRIC_HELP['Качество клиентской базы']} /></div>
          <QualityBar label="Согласны на SMS" count={q.consent_sms_count} pct={q.consent_sms} />
          <QualityBar label="Согласны на Email" count={q.consent_email_count} pct={q.consent_email} />
          <QualityBar label="Заполнено имя" count={q.name_count} pct={q.name} />
          <QualityBar label="Заполнен Email" count={q.email_count} pct={q.email} />
          <QualityBar label="День рождения" count={q.birthdate_count} pct={q.birthdate} />
          <QualityBar label="Заполнен пол" count={q.gender_count} pct={q.gender} />
        </div>

        <div className="card" style={{ flex: 1, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Демография<HelpHint label="Демография" text={METRIC_HELP['Демография']} /></div>
          <p className="dashboard-demography-unknown">Пол не указан: {Math.max(0,(c.registrations.total||0)-(demo.gender?.male||0)-(demo.gender?.female||0)).toLocaleString('ru-RU')} · Дата рождения не заполнена: {Math.max(0,(c.registrations.total||0)-(q.birthdate_count||0)).toLocaleString('ru-RU')}</p>
          <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 20 }}>М</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{(demo.gender?.male || 0).toLocaleString('ru-RU')}</div>
                <div style={{ fontSize: 11, color: 'var(--text-caption)' }}>{demo.gender?.male_pct || 0}%</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 20 }}>Ж</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{(demo.gender?.female || 0).toLocaleString('ru-RU')}</div>
                <div style={{ fontSize: 11, color: 'var(--text-caption)' }}>{demo.gender?.female_pct || 0}%</div>
              </div>
            </div>
          </div>
          <div>
            {(demo.age || []).map(a => (
              <div key={a.range} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ width: 45, fontSize: 11, color: 'var(--text-secondary)' }}>{a.range}</div>
                <div style={{ flex: 1, height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.round(a.count / totalAge * 100)}%`, height: '100%', background: '#8b5cf6', borderRadius: 4 }} />
                </div>
                <div style={{ width: 45, textAlign: 'right', fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{a.count.toLocaleString('ru-RU')}</div>
                <div style={{ width: 30, textAlign: 'right', fontSize: 10, color: 'var(--text-caption)' }}>{Math.round(a.count / totalAge * 100)}%</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}